import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react"
import { FormikProps } from "formik"
import { ReceiptConfirmOrderDialogController } from "../components/receipt/ReceiptConfirmOrderDialogController"
import {
    CreateOrderDialogFlow,
    CreateOrderDialogType,
    SelectedMainUserState,
    AvailableModules,
} from "../createOrderTypes"
import { MeetingServiceOrder } from "../../../../modules/merchant/accounting/accounting/accountingTypes"
import { OutlookBookingSuccessArgs } from "../../../../modules/order/pages/outlookCreateOrderTypes"
import { DynamicMainFormValues, MeetingServiceTabValues } from "../components/form/formTypes"
import { ButtonConfigType } from "../../filters/orderTypeFilter/StandardOrderTypeFilter"
import { computeOrderFormStatus, OrderContextState, OrderFormStatus } from "./helpers"
import { OrderFormContainer } from "./OrderFormContainer"
import { CreateOrderType } from "../helpers"

type OrderFormControllerProps = Readonly<{
    user: SelectedMainUserState
    initialOrderType: CreateOrderType
    dialogType: CreateOrderDialogType
    filteredTabButtonConfig: ButtonConfigType<CreateOrderType>[]
    handleClose: () => void
    flowConfig?: CreateOrderDialogFlow
    orderContext: OrderContextState
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    orderDetails?: MeetingServiceOrder
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
    availableModules: AvailableModules
}>

export function OrderFormController({
    user,
    initialOrderType,
    dialogType,
    filteredTabButtonConfig,
    handleClose,
    flowConfig,
    orderContext,
    setOrderContext,
    orderDetails,
    handleOutlookBookingCompleted,
    availableModules,
}: OrderFormControllerProps) {
    const [confirmOrderDialog, setConfirmOrderDialog] = useState<MeetingServiceTabValues | null>(null)
    const [currentOrderType, setCurrentOrderType] = useState<CreateOrderType | null>(null)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const formRef = useRef<FormikProps<DynamicMainFormValues>>(null)

    const computedStatus = useMemo(() => computeOrderFormStatus(orderDetails), [orderDetails?.id])

    const orderFormStatusRef = useRef<OrderFormStatus>(computedStatus)

    useEffect(() => {
        orderFormStatusRef.current = computedStatus
    }, [computedStatus])

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    const handleSubmitMeetingService = (values: MeetingServiceTabValues) => {
        setConfirmOrderDialog(values)
    }
    const handleOpenResourceBooking = (type: CreateOrderType) => {
        setConfirmOrderDialog(null)
        setCurrentOrderType(type)

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
            setCurrentOrderType(null)
        }, 300)
    }
    const handleAddNewMeetingService = () => {
        setConfirmOrderDialog(null)
        formRef.current?.resetForm()
        orderFormStatusRef.current = computeOrderFormStatus()
    }

    return (
        <>
            <OrderFormContainer
                user={user}
                orderContext={orderContext}
                setOrderContext={setOrderContext}
                orderDetails={orderDetails}
                initialOrderType={initialOrderType}
                newOrderType={currentOrderType}
                dialogType={dialogType}
                filteredTabButtonConfig={filteredTabButtonConfig}
                handleClose={handleClose}
                handleSubmitMeetingService={handleSubmitMeetingService}
                formikRef={formRef}
                flowConfig={flowConfig}
                orderFormStatusRef={orderFormStatusRef}
                handleOutlookBookingCompleted={handleOutlookBookingCompleted}
                availableModules={availableModules}
            />

            {confirmOrderDialog && (
                <ReceiptConfirmOrderDialogController
                    orderContext={orderContext}
                    setOrderContext={setOrderContext}
                    onClose={() => setConfirmOrderDialog(null)}
                    handleCloseMainDialog={handleClose}
                    handleOpenResourceBooking={handleOpenResourceBooking}
                    handleAddNewMeetingService={handleAddNewMeetingService}
                    dialogType={dialogType}
                    orderDetails={confirmOrderDialog}
                    mainUser={user}
                    flowConfig={flowConfig}
                    orderFormStatusRef={orderFormStatusRef}
                    availableModules={availableModules}
                />
            )}
        </>
    )
}
